import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Stack,
  Chip,
  Avatar,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  DirectionsBoat as BoatIcon,
  Person as PersonIcon,
  Build as BuildIcon,
  Warning as WarningIcon,
  Event as EventIcon,
  AssignmentLate as AssignmentLateIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { getStaffMembers } from '../../firebase/services/userService';
import { getVessels } from '../../firebase/services/vesselService';
import { getAllSlips, Slip } from '../../firebase/services/slipService';

const AdminDashboard: React.FC = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();

  // State
  const [slips, setSlips] = useState<Slip[]>([]);
  const [vessels, setVessels] = useState<any[]>([]);
  const [staff, setStaff] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Stats
  const [stats, setStats] = useState({
    totalSlips: 0,
    rentedSlips: 0,
    occupiedSlips: 0,
    availableSlips: 0,
    totalVessels: 0,
    activeVessels: 0,
    staffCount: 0,
    maintenanceDue: 0,
    occupancyRate: 0,
    vesselUtilization: 0
  });

  // Mock data for activity and alerts
  const recentActivity = [
    { type: 'reservation', message: 'New reservation for Slip A-12 by John Doe', time: '2m ago' },
    { type: 'checkin', message: 'Vessel "Sea Breeze" checked in', time: '10m ago' },
    { type: 'maintenance', message: 'Slip B-7 marked for maintenance', time: '1h ago' },
    { type: 'payment', message: 'Payment received from Jane Smith', time: '2h ago' }
  ];
  const alerts = [
    { type: 'registration', message: 'Registration expiring for Vessel "Wave Rider"', severity: 'warning' },
    { type: 'maintenance', message: 'Maintenance due for Slip C-3', severity: 'error' },
    { type: 'payment', message: 'Overdue payment for Slip D-5', severity: 'error' }
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [slipData, vesselData, staffData] = await Promise.all([
          getAllSlips(),
          getVessels(),
          getStaffMembers()
        ]);
        setSlips(slipData);
        setVessels(vesselData);
        setStaff(staffData);
        // Calculate stats
        const totalSlips = slipData.length;
        const rentedSlips = slipData.filter(s => !!s.customerId).length;
        const occupiedSlips = slipData.filter(s => !!s.assignedVesselId).length;
        const availableSlips = slipData.filter(s => !s.customerId).length;
        const totalVessels = vesselData.length;
        const activeVessels = vesselData.filter((v: any) => v.isActive).length;
        const staffCount = staffData.length;
        const maintenanceDue = slipData.filter(s => s.status === 'maintenance').length;
        const occupancyRate = totalSlips ? Math.round((rentedSlips / totalSlips) * 100) : 0;
        const vesselUtilization = totalVessels ? Math.round((activeVessels / totalVessels) * 100) : 0;
        setStats({
          totalSlips,
          rentedSlips,
          occupiedSlips,
          availableSlips,
          totalVessels,
          activeVessels,
          staffCount,
          maintenanceDue,
          occupancyRate,
          vesselUtilization
        });
      } catch (error) {
        // handle error
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Typography variant="h4" gutterBottom>Marina Dashboard</Typography>
      <Typography variant="subtitle1" gutterBottom>Welcome, {userProfile?.firstName || 'Admin'}!</Typography>
      {/* Top Row: Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <Card 
            sx={{ 
              bgcolor: 'info.light', 
              color: 'info.dark', 
              textAlign: 'center',
              cursor: 'pointer',
              height: '160px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              '&:hover': {
                bgcolor: 'info.main',
                color: 'info.contrastText',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
            onClick={() => navigate('/admin/slips?filter=all')}
          >
            <CardContent>
              <BoatIcon fontSize="large" />
              <Typography variant="h5">{stats.totalSlips}</Typography>
              <Typography variant="subtitle2">Total Slips</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card 
            sx={{ 
              bgcolor: 'primary.light', 
              color: 'primary.dark', 
              textAlign: 'center',
              cursor: 'pointer',
              height: '160px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              '&:hover': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
            onClick={() => navigate('/admin/slips?filter=rented')}
          >
            <CardContent>
              <BoatIcon fontSize="large" />
              <Typography variant="h5">{stats.rentedSlips}</Typography>
              <Typography variant="subtitle2">Rented Slips</Typography>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                Revenue: ${(stats.rentedSlips * 2750).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card 
            sx={{ 
              bgcolor: 'success.light', 
              color: 'success.dark', 
              textAlign: 'center',
              cursor: 'pointer',
              height: '160px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              '&:hover': {
                bgcolor: 'success.main',
                color: 'success.contrastText',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
            onClick={() => navigate('/admin/slips?filter=occupied')}
          >
            <CardContent>
              <BoatIcon fontSize="large" />
              <Typography variant="h5">{stats.occupiedSlips}</Typography>
              <Typography variant="subtitle2">Occupied Slips</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card 
            sx={{ 
              bgcolor: 'warning.light', 
              color: 'warning.dark', 
              textAlign: 'center',
              cursor: 'pointer',
              height: '160px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              '&:hover': {
                bgcolor: 'warning.main',
                color: 'warning.contrastText',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
            onClick={() => navigate('/admin/slips?filter=available')}
          >
            <CardContent>
              <BoatIcon fontSize="large" />
              <Typography variant="h5">{stats.availableSlips}</Typography>
              <Typography variant="subtitle2">Available Slips</Typography>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                Lost Revenue: ${(stats.availableSlips * 2750).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card 
            sx={{ 
              bgcolor: 'info.light', 
              color: 'info.dark', 
              textAlign: 'center',
              cursor: 'pointer',
              height: '160px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              '&:hover': {
                bgcolor: 'info.main',
                color: 'info.contrastText',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
            onClick={() => navigate('/admin/equipment/vessels')}
          >
            <CardContent>
              <BoatIcon fontSize="large" />
              <Typography variant="h5">{stats.totalVessels}</Typography>
              <Typography variant="subtitle2">Total Vessels</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card 
            sx={{ 
              bgcolor: 'success.light', 
              color: 'success.dark', 
              textAlign: 'center',
              cursor: 'pointer',
              height: '160px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              '&:hover': {
                bgcolor: 'success.main',
                color: 'success.contrastText',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
            onClick={() => navigate('/admin/equipment/vessels?filter=active')}
          >
            <CardContent>
              <BoatIcon fontSize="large" />
              <Typography variant="h5">{stats.activeVessels}</Typography>
              <Typography variant="subtitle2">Active Vessels</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card 
            sx={{ 
              bgcolor: 'secondary.light', 
              color: 'secondary.dark', 
              textAlign: 'center',
              cursor: 'pointer',
              height: '160px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              '&:hover': {
                bgcolor: 'secondary.main',
                color: 'secondary.contrastText',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
            onClick={() => navigate('/admin/staff')}
          >
            <CardContent>
              <PersonIcon fontSize="large" />
              <Typography variant="h5">{stats.staffCount}</Typography>
              <Typography variant="subtitle2">Staff</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card 
            sx={{ 
              bgcolor: 'error.light', 
              color: 'error.dark', 
              textAlign: 'center',
              cursor: 'pointer',
              height: '160px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              '&:hover': {
                bgcolor: 'error.main',
                color: 'error.contrastText',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
            onClick={() => navigate('/admin/maintenance')}
          >
            <CardContent>
              <BuildIcon fontSize="large" />
              <Typography variant="h5">{stats.maintenanceDue}</Typography>
              <Typography variant="subtitle2">Maintenance Due</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Second Row: Charts and Alerts */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>Occupancy Rate</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: '100%' }}>
                <LinearProgress variant="determinate" value={stats.occupancyRate} sx={{ height: 16, borderRadius: 8 }} />
                <Typography variant="body2" sx={{ mt: 1 }}>{stats.occupancyRate}% of slips rented</Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>Vessel Utilization</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: '100%' }}>
                <LinearProgress color="success" variant="determinate" value={stats.vesselUtilization} sx={{ height: 16, borderRadius: 8 }} />
                <Typography variant="body2" sx={{ mt: 1 }}>{stats.vesselUtilization}% of vessels active</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>Alerts & Reminders</Typography>
            <Stack spacing={2}>
              {alerts.map((alert, idx) => (
                <Alert key={idx} severity={alert.severity as any} iconMapping={{ warning: <WarningIcon />, error: <AssignmentLateIcon /> }}>
                  {alert.message}
                </Alert>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
      {/* Third Row: Activity Feed and Quick Actions */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>Recent Activity</Typography>
            <List>
              {recentActivity.map((item, idx) => (
                <ListItem key={idx}>
                  <ListItemText primary={item.message} secondary={item.time} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>Quick Actions</Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/admin/settings/marina')}>Add Slip</Button>
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/admin/equipment/vessels/new')}>Add Vessel</Button>
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/admin/reservations/new')}>Add Reservation</Button>
              <Button variant="contained" color="primary" startIcon={<BuildIcon />} onClick={() => navigate('/admin/maintenance')}>Add Maintenance</Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard; 